import { device } from '@/styles/common';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';

function getAccountShortened(account: any) {
  if (!account) {
    return null;
  }

  return (
    account.ensName ||
    `${account.address.slice(0, 6)}...${account.address.slice(
      account.address.length - 3,
      account.address.length,
    )}`
  );
}

function AccountStatus() {
  return (
    <ConnectButton.Custom>
      {({ mounted, account, openAccountModal }) => {
        const isVisible = account && mounted;
        return (
          <AnimatePresence>
            {isVisible && (
              <AccountStatusOuterContainer
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ delay: 0.5 }}
              >
                <AccountStatusInnerContainer onClick={openAccountModal}>
                  <Dot />
                  <Text>Connected as {getAccountShortened(account)}</Text>
                </AccountStatusInnerContainer>
              </AccountStatusOuterContainer>
            )}
          </AnimatePresence>
        );
      }}
    </ConnectButton.Custom>
  );
}

const AccountStatusOuterContainer = styled(motion.div)`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  width: 100%;

  width: 542px;
  margin-bottom: 12px;

  @media ${device.mobileS} {
    width: calc(100vw - 32px);
  }
`;

const AccountStatusInnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  padding: 6px 12px;
  gap: 6px;
  background: #212738;
  cursor: pointer;
  &:hover {
    background: rgba(39, 44, 57, 0.85);
  }
  transition: background 0.2s ease-in-out;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  background: #1e873c;
  border-radius: 50%;
`;

const Text = styled.div`
  font-size: 14px;
  line-height: 1.5;
`;

export default AccountStatus;

/* Here is the explanation for the code above:

1. We import the ConnectButton component from the rainbowkit package. This component is a button that can be used to connect to a web3 provider.
2. We import the AnimatePresence and motion components from the framer-motion package. The AnimatePresence component is used to animate components when they are added or removed from the DOM. The motion component is used to animate components.
3. We import the styled component from the styled-components package. This package allows us to write CSS within JavaScript.
4. We import the device object from the styles/common.ts file. This object contains the media queries that we use for responsive design.
5. We create a function named getAccountShortened that takes an account as an argument. This function returns the account's ENS name or the first six and last three characters of the account's address.
6. We create a function component named AccountStatus that takes no arguments. This component renders the ConnectButton component, an AnimatePresence component, and an AccountStatusOuterContainer component.
7. The ConnectButton component is used to connect to a web3 provider. The ConnectButton component takes a render prop that is a function that takes two arguments: mounted and account. The mounted argument is a boolean that indicates if the web3 provider is mounted. The account argument is an object that contains the account's address and ENS name.
8. The AnimatePresence component is used to animate components when they are added or removed from the DOM. The AnimatePresence component takes a single child that is wrapped in curly brackets. The child is rendered if the web3 provider is mounted and the account is not null.
9. The AccountStatusOuterContainer component is used to render the AccountStatusInnerContainer component. The AccountStatusOuterContainer component is a styled div that uses the motion component to animate the component.
10. The AccountStatusInnerContainer component is used to render the Dot and Text components. The AccountStatusInnerContainer component is a styled div that uses the motion component to animate the component.
11. The Dot component is a styled div that is used to render a dot.
12. The Text component is a styled div that is used to render text.
13. We export the AccountStatus component as the default export.

*/
